/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import InputLabel from 'components/generics/InputLabel/InputLabel';
import Input from 'components/generics/Input/Input';
import Select from 'components/generics/Select/Select';
import MultiSelect from 'components/generics/MultiSelect/MultiSelect';
import Button from 'components/generics/Button/Button';
import Delay from 'components/pages/Flights/Delays';
import AttachmentsSection from 'components/pages/Flights/AttachmentsSection';
import ChecklistSection from 'components/generics/Checklist/ChecklistSection';
import {
  formatDate,
  formatDateForInputs,
  formatMinutesToReadableHourOfDay,
  formatReadableHourToMinutes,
  updateForm,
} from 'utils/func';
import { DAYS_MINUTES, FLIGHT_STATUS_NAMES } from 'utils/const';

import { get as getAirports } from 'services/API/Airport';
import { get as getMissions } from 'services/API/Mission';
import { get as getAircrafts } from 'services/API/Aircraft';
import { get as getAircraftTypes } from 'services/API/AircraftType';
import { get as getRoutes } from 'services/API/Route';
import { get as getFlightStatus } from 'services/API/FlightStatus';
import { get as getStages } from 'services/API/Stage';
import { getTemplates, getFlightTemplates, setFlightTemplates } from 'services/API/Checklist';
import { add as addFlight, edit as editFlight, getById as getFlightsById } from 'services/API/Flight';

import FuelIcon from 'components/icons/FuelIcon';
import StatusIcon from 'components/icons/StatusIcon';
import DateIcon from 'components/icons/DateIcon';
import TakeOffIcon from 'components/icons/TakeOffIcon';
import LandingIcon from 'components/icons/LandingIcon';
import TLBTimeIcon from 'components/icons/TBLTimeIcon';
import DelayIcon from 'components/icons/DelayIcon';

/**
 * Minutes -> 'HH:MM' exploitable par un input type="time".
 * Retourne '' si la valeur n'est pas un nombre valide.
 */
const formatMinutesAsTimeValue = minutes => {
    if (typeof minutes !== 'number' || isNaN(minutes) || minutes < 0) return '';
    const normalized = minutes % DAYS_MINUTES;
    const hours = Math.floor(normalized / 60).toString().padStart(2, '0');
    const mins = (normalized % 60).toString().padStart(2, '0');
    return `${hours}:${mins}`;
};

const FlightForm = ({onSubmit, editId, type}) => {
    const isEdit = ['edit', 'editASO', 'editCREW'].includes(type);
    const DEFAULT_VALUES = {
        date: +new Date(),
        departureAirportId: 0,
        takeoffTimeUtc: '00:00',
        arrivalAirportId: 0,
        landingTimeUtc: '00:00',
        missionId: 0,
        aircraftId: 0,
        passengerCapacity: 0,
        // Renseigné une fois les statuts de la course courante chargés (cf. useEffect)
        flightStatusId: 0,
        stageId: 0,
        checklistTemplateId: 0,
        remark: '',
        calculateLandingTime: true,
        passengerPlanned: 0,
        // TLB saisi manuellement (hélicoptères : plusieurs décollages par vol)
        tlbManual: false,
    };

    const [formDatas, setFormDatas] = useState(DEFAULT_VALUES);
    const [aircrafts, setAircrafts] = useState([]);
    const [aircraftTypes, setAircraftTypes] = useState([]);
    const [airports, setAirports] = useState([]);
    const [missions, setMissions] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [flightStatus, setFlightStatus] = useState([]);
    const [stages, setStages] = useState([]);
    const [checklistModels, setChecklistModels] = useState([]);
    // Modèles cochés : un modèle coché = une checklist générée pour ce vol.
    const [checklistTemplateIds, setChecklistTemplateIds] = useState([]);
    const [stagePopup, setStagePopup] = useState(null);
    // Heures TLB mémorisées avant de passer en saisie manuelle
    const [tlbTimesBackup, setTlbTimesBackup] = useState(null);
    const [entityToLoad] = useState(['edit', 'editASO', 'editCREW'].includes(type) ? 8 : 7);
    const [loading, setLoading] = useState(true);

    let loadedEntity = 0;

    const entityLoaded = () => {
        loadedEntity++;
        if (loadedEntity === entityToLoad) {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (
            formDatas.calculateLandingTime &&
            formDatas.departureAirportId !== 0 &&
            formDatas.arrivalAirportId !== 0 &&
            formDatas.aircraftId !== 0 &&
            formDatas.departureAirportId !== formDatas.arrivalAirportId &&
            !loading
        ) {
            const aircraftTypeId = aircraftTypes.filter(
                v =>
                    v.id ===
                    aircrafts.filter(t => t.id === formDatas.aircraftId)[0]
                        .aircraftTypeId
            )[0].id;
            const matchingRoutes = retrieveRoute(
                +formDatas.departureAirportId,
                +formDatas.arrivalAirportId,
                aircraftTypeId);

            if (matchingRoutes.length > 0) {
                updateForm(
                    formDatas,
                    setFormDatas,
                    'landingTimeUtc',
                    formatMinutesToReadableHourOfDay(
                        (formatReadableHourToMinutes(formDatas.takeoffTimeUtc) +
                            +matchingRoutes[0].duration) %
                        DAYS_MINUTES),
                    );
                }
            }
        }
    , [
        formDatas.departureAirportId,
        formDatas.arrivalAirportId,
        formDatas.takeoffTimeUtc,
        formDatas.aircraftId,
        formDatas.calculateLandingTime,
        loading
    ]);

    const retrieveRoute = (from, to, aircraft) =>
        routes
            .filter(v => v.aircraftTypeId === aircraft)
            .filter(
                v => v.departureAirportId === from || v.arrivalAirportId === from
            )
            .filter(v => v.arrivalAirportId === to || v.departureAirportId === to);

    useEffect(() => {
        // todo: use promise.all
        getAirports()
            .then(resAirports => {
                setAirports([
                    ...[{icaoCode: 'Select an airport', id: 0}],
                    ...resAirports.data]);

                entityLoaded();
            })
            .catch(e => toast.error(e.message));
        getMissions()
            .then(resMissions => {
                setMissions([
                    ...[{name: 'Select a mission', id: 0}],
                    ...resMissions.data]);

                entityLoaded();
            })
            .catch(e => toast.error(e.message));
        getAircrafts()
            .then(resAircrafts => {
                setAircrafts([
                    ...[{reg: 'Select an aircraft', id: 0}],
                    ...resAircrafts.data
                ]);
                entityLoaded();
            })
            .catch(e => toast.error(e.message));
        getRoutes()
            .then(resRoutes => {
                setRoutes(resRoutes.data);
                entityLoaded();
            })
            .catch(e => toast.error(e.message));
        getAircraftTypes()
            .then(resAircraftTypes => {
                setAircraftTypes(resAircraftTypes.data);
                entityLoaded();
            })
            .catch(e => toast.error(e.message));
        getStages()
            .then(resStages => {
                setStages(
                    resStages.data.map(stage => ({
                        ...stage,
                        label: `${stage.name} - ${formatDate(new Date(stage.date))} (${
                            stage.departureAirport?.icaoCode
                        }→${stage.arrivalAirport?.icaoCode})`,
                    })),
                );
                entityLoaded();
            })
            .catch(e => toast.error(e.message));

        if (['edit', 'editASO', 'editCREW'].includes(type)) {
            getFlightsById(editId)
                .then(resFlight => {
                    let formatedData = {
                        ...{
                            id: resFlight.data.id,
                            date: new Date(resFlight.data.date),
                            takeoffTimeUtc: formatMinutesToReadableHourOfDay(
                                +resFlight.data.takeoffTimeUtc
                            ),
                            landingTimeUtc: formatMinutesToReadableHourOfDay(
                                +resFlight.data.landingTimeUtc),

                            // Les heures TLB sont null en saisie manuelle (ou si
                            // elles n'ont jamais été renseignées).
                            landingTimeTlb:
                                resFlight.data.landingTimeTlb === null ||
                                resFlight.data.landingTimeTlb === undefined
                                    ? ''
                                    : formatMinutesAsTimeValue(
                                          +resFlight.data.landingTimeTlb
                                      ),

                            takeoffTimeTlb:
                                resFlight.data.takeoffTimeTlb === null ||
                                resFlight.data.takeoffTimeTlb === undefined
                                    ? ''
                                    : formatMinutesAsTimeValue(
                                          +resFlight.data.takeoffTimeTlb
                                      ),
                            // TLB manuel : la valeur enregistrée fait foi, sinon
                            // elle est recalculée à partir des heures TLB.
                            tlbManual: !!resFlight.data.tlbManual,
                            flightTimeTlb: resFlight.data.flightTimeTlb
                                ? formatMinutesAsTimeValue(
                                      +resFlight.data.flightTimeTlb
                                  )
                                : '',
                            landingTimeReal: formatMinutesToReadableHourOfDay(
                                +resFlight.data.landingTimeReal),

                            takeoffTimeReal: formatMinutesToReadableHourOfDay(
                                +resFlight.data.takeoffTimeReal
                            ),
                            remark: resFlight.data.remark,
                            passengerCapacity: resFlight.data.passengerCapacity,
                            departureAirportId: resFlight.data.departureAirportId,
                            arrivalAirportId: resFlight.data.arrivalAirportId,
                            missionId: resFlight.data.missionId,
                            aircraftId: resFlight.data.aircraftId,
                            passengerCounter: resFlight.data.passengerCounter,
                            fuelOnDeparture: resFlight.data.fuelOnDeparture,
                            fuelOnArrival: resFlight.data.fuelOnArrival,
                            flightStatusId: resFlight.data.flightStatusId,
                            passengerPlanned: resFlight.data.passengerPlanned,
                            stageId: resFlight.data.stageId ?? 0,
                            checklistTemplateId: resFlight.data.checklistTemplateId ?? 0,
                        },
                    };
                    setFormDatas(formatedData);
                    entityLoaded();
                    // Modèles cochés (table dédiée : plusieurs modèles par vol)
                    if (type !== 'editASO') {
                        getFlightTemplates(resFlight.data.id)
                            .then(res =>
                                setChecklistTemplateIds((res.data || []).map(model => model.id)),
                            )
                            .catch(() => {});
                    }
                })
                .catch(e => toast.error(e.message));
        }

        // Les statuts sont scopés par course : on les charge dans tous les cas et,
        // en création, on présélectionne « Validated » (jamais un id en dur).
        getFlightStatus()
            .then(resFlightStatus => {
                const statuses = resFlightStatus.data || [];
                setFlightStatus(statuses);
                if (!isEdit && statuses.length > 0) {
                    const validated = statuses.find(
                        status =>
                            (status.name || '').toLowerCase() ===
                            FLIGHT_STATUS_NAMES.VALIDATED.toLowerCase(),
                    );
                    setFormDatas(prev => ({
                        ...prev,
                        flightStatusId: (validated || statuses[0]).id,
                    }));
                }
                entityLoaded();
            })
            .catch(e => toast.error(e.message));
    }, []);

    // Modèles de checklist proposés dans le sélecteur (scopés par course).
    // Non chargés en mode ASO : ce rôle n'a pas accès aux modèles.
    useEffect(() => {
        if (type === 'editASO') {
            return;
        }
        getTemplates()
            .then(res => setChecklistModels(res.data || []))
            .catch(e => toast.error(e.message));
    }, []);

    const prepareDate = date =>
        `${date.getFullYear()}-${(date.getMonth() % 12) + 1}-${date.getDate()}`;

    /**
     * Modèles cochés dans la liste déroulante. `formDatas.checklistTemplateId`
     * reste synchronisé sur le premier modèle coché (colonne miroir côté API).
     */
    const changeChecklistTemplates = templateIds => {
        setChecklistTemplateIds(templateIds);
        updateForm(formDatas, setFormDatas, 'checklistTemplateId', templateIds[0] ?? 0);
    };

    /** Enregistre les modèles cochés (après création / édition du vol). */
    const saveChecklistTemplates = flightId =>
        // ASO n'a pas accès aux modèles : ne jamais écraser la sélection.
        flightId && type !== 'editASO'
            ? setFlightTemplates(flightId, checklistTemplateIds)
            : Promise.resolve();

    const handleStageChange = value => {
        const stageId = +value;
        updateForm(formDatas, setFormDatas, 'stageId', stageId);
        const stage = stages.find(s => s.id === stageId);
        setStagePopup(stage || null);
    };

    const prefillFromStage = () => {
        if (!stagePopup) return;
        setFormDatas({
            ...formDatas,
            date: +new Date(stagePopup.date),
            departureAirportId: stagePopup.departureAirportId,
            arrivalAirportId: stagePopup.arrivalAirportId,
        });
        setStagePopup(null);
    };

    /**
     * Temps de vol TLB déduit des heures TLB, comme le fait l'API
     * (passage de minuit géré). Retourne null si les heures sont incomplètes.
     */
    const computeTlbFlightTime = () => {
        if (!formDatas.takeoffTimeTlb || !formDatas.landingTimeTlb) return null;
        const diff =
            formatReadableHourToMinutes(formDatas.landingTimeTlb) -
            formatReadableHourToMinutes(formDatas.takeoffTimeTlb);
        return diff < 0 ? diff + DAYS_MINUTES : diff;
    };

    /**
     * Bascule entre TLB calculé (heures de décollage / atterrissage) et TLB
     * saisi à la main. En saisie manuelle les heures TLB ne sont plus utilisées :
     * elles sont vidées (sauvegardées pour pouvoir les restaurer si on décoche),
     * et le temps de vol est pré-rempli avec la valeur calculée.
     */
    const toggleTlbManual = () => {
        const tlbManual = !formDatas.tlbManual;
        if (tlbManual) {
            setTlbTimesBackup({
                takeoffTimeTlb: formDatas.takeoffTimeTlb,
                landingTimeTlb: formDatas.landingTimeTlb,
            });
        }
        setFormDatas({
            ...formDatas,
            tlbManual,
            takeoffTimeTlb: tlbManual
                ? ''
                : tlbTimesBackup?.takeoffTimeTlb || '',
            landingTimeTlb: tlbManual
                ? ''
                : tlbTimesBackup?.landingTimeTlb || '',
            flightTimeTlb: tlbManual
                ? formDatas.flightTimeTlb ||
                  formatMinutesAsTimeValue(computeTlbFlightTime())
                : formDatas.flightTimeTlb,
        });
        if (!tlbManual) setTlbTimesBackup(null);
    };

    const validateData = () => {
        if (type === 'edit' || type === 'editCREW') {
            if (
                formDatas.departureAirportId !== 0 &&
                formDatas.arrivalAirportId !== 0 &&
                formDatas.missionId !== 0 &&
                formDatas.aircraftId !== 0) {

                let formatedData = {
                    ...formDatas,
                    ...{
                        date: prepareDate(new Date(formDatas.date)),
                        takeoffTimeUtc: formatReadableHourToMinutes(
                            formDatas.takeoffTimeUtc),

                        landingTimeUtc: formatReadableHourToMinutes(
                            formDatas.landingTimeUtc),

                        landingTimeTlb: formDatas.tlbManual
                            ? null
                            : formatReadableHourToMinutes(
                                  formDatas.landingTimeTlb
                              ),
                        takeoffTimeTlb: formDatas.tlbManual
                            ? null
                            : formatReadableHourToMinutes(
                                  formDatas.takeoffTimeTlb
                              ),
                        // TLB manuel : la valeur saisie est enregistrée telle
                        // quelle, sinon l'API la recalcule (d'où le null).
                        tlbManual: !!formDatas.tlbManual,
                        flightTimeTlb:
                            formDatas.tlbManual && formDatas.flightTimeTlb
                                ? formatReadableHourToMinutes(
                                      formDatas.flightTimeTlb
                                  )
                                : null,
                        landingTimeReal: formatReadableHourToMinutes(
                            formDatas.landingTimeReal
                        ),
                        takeoffTimeReal: formatReadableHourToMinutes(
                            formDatas.takeoffTimeReal),
                        stageId: formDatas.stageId === 0 ? null : formDatas.stageId,
                        checklistTemplateId:
                            formDatas.checklistTemplateId === 0
                                ? null
                                : +formDatas.checklistTemplateId,
                        },
                    };

                delete formatedData.id;
                delete formatedData.calculateLandingTime;
                editFlight(formatedData, formDatas.id)
                    .then(() => saveChecklistTemplates(formDatas.id))
                    .then(() => {
                        toast.success('Flight edited');
                        setFormDatas(DEFAULT_VALUES);
                        onSubmit(formDatas);
                    })
                    .catch(err => toast.error(err.message));
            } else {
                toast.error('Fields missing'); // TODO: improve message
            }
        } else {
            if (
                formDatas.departureAirportId !== 0 &&
                formDatas.arrivalAirportId !== 0 &&
                formDatas.missionId !== 0 &&
                formDatas.aircraftId !== 0) {

                let formatedData = {
                    ...formDatas,
                    takeoffTimeUtc: formatReadableHourToMinutes(
                        formDatas.takeoffTimeUtc
                    ),
                    landingTimeUtc: formatReadableHourToMinutes(
                        formDatas.landingTimeUtc
                    ),
                    hasBeenRead: true,
                    date: prepareDate(new Date(formDatas.date)),
                    stageId: formDatas.stageId === 0 ? null : formDatas.stageId,
                    checklistTemplateId:
                        formDatas.checklistTemplateId === 0
                            ? null
                            : +formDatas.checklistTemplateId,
                };
                delete formatedData.calculateLandingTime;
                addFlight(formatedData)
                    .then(res => saveChecklistTemplates(res?.data?.id))
                    .then(() => {
                        toast.success('Flight added');
                        setFormDatas(DEFAULT_VALUES);
                        onSubmit(formDatas);
                    })
                    .catch(err => toast.error(err.message));            } else {
                toast.error('Fields missing'); // TODO: improve message
            }
        }
    };

  const action = () => (type === 'edit' || type === 'editASO' || type === 'editCREW' ? 'Edit' : 'Add a');

    return !loading ? (
        <div className="modal__content">
            <h2>{action()} flight</h2>
            <div className="form-part part-one">
                <div>
          <span>
            <DateIcon/>
            <h3>CALENDAR</h3>
          </span>
                    <InputLabel label="DATE" name="date">
                        <Input
                            name="date"
                            type="date"
                            onChange={value =>
                                updateForm(formDatas, setFormDatas, 'date', +new Date(value))
                            }
                            value={formatDateForInputs(new Date(formDatas.date))}
                            disabled={['editASO', 'editCREW'].includes(type)}
                        />
                    </InputLabel>
                    <InputLabel label="STAGE" name="stage">
                        <Select
                            name="stage"
                            options={
                                stages.length > 0
                                    ? [{ label: 'Aucun stage', id: 0 }, ...stages]
                                    : [{ label: "Aucun stage n'existe", id: 0 }]
                            }
                            itemValue="id"
                            itemLabel="label"
                            value={formDatas.stageId}
                            onChange={handleStageChange}
                            disabled={stages.length === 0 || ['editASO', 'editCREW'].includes(type)}
                        />
                    </InputLabel>
                    <InputLabel label="CHECKLIST MODELS" name="checklistTemplates">
                        <MultiSelect
                            name="checklistTemplates"
                            options={checklistModels}
                            values={checklistTemplateIds}
                            onChange={changeChecklistTemplates}
                            placeholder="Aucun modèle"
                            emptyLabel="Aucun modèle disponible"
                            disabled={['editASO', 'editCREW'].includes(type)}
                        />
                    </InputLabel>
                </div>
                <div>
          <span>
            <TakeOffIcon/>
            <h3>DEPARTURE</h3>
          </span>
                    <InputLabel label="FROM (ICAO)" name="departureAirport">
                        <Select
                            name="departureAirport"
                            options={airports}
                            value={formDatas.departureAirportId}
                            itemValue="id"
                            itemLabel="icaoCode"
                            onChange={value =>
                                updateForm(
                                    formDatas,
                                    setFormDatas,
                                    'departureAirportId',
                                    +value)}

                            disabled={['editASO', 'editCREW'].includes(type)}
                        />
                    </InputLabel>
                    <InputLabel label="TAKE OFF TIME (UTC)" name="takeoffTimeUtc">
                        <Input
                            name="takeoffTimeUtc"
                            type="time"
                            placeholder="00:00"
                            onChange={value =>
                                updateForm(formDatas, setFormDatas, 'takeoffTimeUtc', value)}

                            value={formDatas.takeoffTimeUtc}
                            disabled={['editASO', 'editCREW'].includes(type)}
                        />
                    </InputLabel>
                    {type === 'edit' && (
                        <InputLabel label="ACT TAKE OFF" name="takeoffTimeReal">
                            <Input
                                name="takeoffTimeReal"
                                type="time"
                                placeholder="00:00"
                                onChange={value =>
                                    updateForm(formDatas, setFormDatas, 'takeoffTimeReal', value)
                                }
                                value={formDatas.takeoffTimeReal}
                                disabled={['editASO', 'editCREW'].includes(type)}
                            />
                        </InputLabel>
                    )}
                </div>
                <div>
          <span>
            <LandingIcon/>
            <h3>ARRIVAL</h3>
          </span>
                    <InputLabel label="TO (ICAO)" name="arrivalAirport">
                        <Select
                            name="arrivalAirport"
                            options={airports}
                            itemValue="id"
                            itemLabel="icaoCode"
                            onChange={value =>
                                updateForm(formDatas, setFormDatas, 'arrivalAirportId', +value)
                            }
                            value={formDatas.arrivalAirportId}
                            disabled={['editASO', 'editCREW'].includes(type)}
                        />
                    </InputLabel>
                    <InputLabel label="LANDING TIME (UTC)" name="landingTimeUtc">
                        <>
                            <label name="toggleCalcul" className="calcul">
                                <input
                                    name="toggleCalcul"
                                    type="checkbox"
                                    onChange={() =>
                                        updateForm(
                                            formDatas,
                                            setFormDatas,
                                            'calculateLandingTime',
                                            !formDatas.calculateLandingTime
                                        )
                                    }
                                    checked={formDatas.calculateLandingTime}
                                    disabled={['editASO', 'editCREW'].includes(type)}
                                />
                                Auto calcul
                            </label>
                            <Input
                                name="landingTimeUtc"
                                type="time"
                                disabled={
                                    ['editASO', 'editCREW'].includes(type) ||
                                    formDatas.calculateLandingTime
                                }
                                placeholder="00:00"
                                onChange={value =>
                                    updateForm(formDatas, setFormDatas, 'landingTimeUtc', value)
                                }
                                value={formDatas.landingTimeUtc}
                            />
                        </>
                    </InputLabel>
                    {type === 'edit' && (
                        <InputLabel label="ACT LANDING" name="landingTimeReal">
                            <Input
                                name="landingTimeREAL"
                                type="time"
                                placeholder="00:00"
                                onChange={value =>
                                    updateForm(formDatas, setFormDatas, 'landingTimeReal', value)
                                }
                                value={formDatas.landingTimeReal}
                                disabled={['editASO', 'editCREW'].includes(type)}
                            />
                        </InputLabel>
                    )}
                </div>
            </div>
            {['edit', 'editASO', 'editCREW'].includes(type) && (
                <div className="form-part part-edit">
                    <div className="TLB section">
            <span className="title">
              <span>
                <TLBTimeIcon width={25}/>
                TLB TIME
                <label className="calcul">
                  <input
                      name="tlbManual"
                      type="checkbox"
                      onChange={toggleTlbManual}
                      checked={!!formDatas.tlbManual}
                      disabled={['editASO'].includes(type)}
                  />
                  Saisie manuelle
                </label>
              </span>
            </span>
                        <span className="inputs">
              <InputLabel label="TAKE OFF TIME (TLB)" name="takeofftimetlb">
                <Input
                    name="takeofftimetlb"
                    type="time"
                    placeholder="00:00"
                    onChange={value =>
                        updateForm(formDatas, setFormDatas, 'takeoffTimeTlb', value)
                    }
                    value={formDatas.takeoffTimeTlb}
                    disabled={
                        ['editASO'].includes(type) || !!formDatas.tlbManual
                    }
                />
              </InputLabel>
              <InputLabel label="LANDING TIME (TLB)" name="landingtimetlb">
                <Input
                    name="landingtimetlb"
                    type="time"
                    placeholder="00:00"
                    onChange={value =>
                        updateForm(formDatas, setFormDatas, 'landingTimeTlb', value)
                    }
                    value={formDatas.landingTimeTlb}
                    disabled={
                        ['editASO'].includes(type) || !!formDatas.tlbManual
                    }
                />
              </InputLabel>
              <InputLabel label="FLIGHT TIME (TLB)" name="flighttimetlb">
                <Input
                    name="flighttimetlb"
                    type="time"
                    placeholder="00:00"
                    onChange={value =>
                        updateForm(formDatas, setFormDatas, 'flightTimeTlb', value)
                    }
                    // Saisie manuelle : champ éditable et valeur enregistrée.
                    // Sinon : calcul automatique à partir des heures TLB.
                    value={
                        formDatas.tlbManual
                            ? formDatas.flightTimeTlb || ''
                            : formatMinutesAsTimeValue(computeTlbFlightTime())
                    }
                    disabled={
                        ['editASO'].includes(type) || !formDatas.tlbManual
                    }
                />
              </InputLabel>
            </span>
                    </div>
                    <div className="delay section">
            <span className="title">
              <span>
                <DelayIcon width={30}/>
                DELAY(S)
              </span>
            </span>
                        <span className="inputs">
              <Delay flightId={editId}/>
            </span>
                    </div>
                    <div className="fuel section">
            <span className="title">
              <span>
                <FuelIcon width={35} fill="#03A9F4"/>
                FUEL
              </span>
            </span>
                        <span className="inputs">
              <InputLabel label="FUEL UPLIFT on departure Airport (L)" name="dueldeparture">
                <Input
                    name="dueldeparture"
                    type="text"
                    placeholder=""
                    onChange={value =>
                        updateForm(
                            formDatas,
                            setFormDatas,
                            'fuelOnDeparture',
                            +value
                        )
                    }
                    value={formDatas.fuelOnDeparture}
                    disabled={['editASO'].includes(type)}
                />
              </InputLabel>
              <InputLabel label="FUEL UPLIFT on arrival airport (L)" name="duelarrival">
                <Input
                    name="duelarrival"
                    type="text"
                    placeholder=""
                    onChange={value =>
                        updateForm(formDatas, setFormDatas, 'fuelOnArrival', +value)
                    }
                    value={formDatas.fuelOnArrival}
                    disabled={['editASO'].includes(type)}
                />
              </InputLabel>
            </span>
                    </div>
                    <div className="status section">
            <span className="title">
              <span>
                <StatusIcon width={30}/>
                STATUS
              </span>
            </span>
                        <span className="inputs">
              <InputLabel label="STATUS" name="flightstatus">
                <Select
                    name="flightstatus"
                    options={flightStatus}
                    itemValue="id"
                    itemLabel="name"
                    className="primary"
                    onChange={value =>
                        updateForm(
                            formDatas,
                            setFormDatas,
                            'flightStatusId',
                            +value
                        )
                    }
                    value={formDatas.flightStatusId}
                    disabled={['editASO', 'editCREW'].includes(type)}
                />
              </InputLabel>
              <InputLabel label="PAX planned" name="seatsPlanned">
                <Input
                    name="seatsPlanned"
                    type="text"
                    placeholder=""
                    defaultStyle="primary"
                    onChange={value =>
                        updateForm(
                            formDatas,
                            setFormDatas,
                            'passengerPlanned',
                            +value
                        )
                    }
                    value={formDatas.passengerPlanned}
                />
              </InputLabel>
              <InputLabel label="PAX" name="seats">
                <Input
                    name="seats"
                    type="text"
                    placeholder=""
                    defaultStyle="primary"
                    onChange={value =>
                        updateForm(
                            formDatas,
                            setFormDatas,
                            'passengerCounter',
                            +value
                        )
                    }
                    value={formDatas.passengerCounter}
                    disabled={['editASO'].includes(type)}
                />
              </InputLabel>
            </span>
                    </div>
                </div>
            )}
            <div className="form-part part-complement">
                <div className="top">
                    <div>
                        <InputLabel label="MISSION" name="mission">
                            <Select
                                name="mission"
                                options={missions}
                                itemValue="id"
                                itemLabel="name"
                                className="primary"
                                onChange={value =>
                                    updateForm(formDatas, setFormDatas, 'missionId', +value)
                                }
                                value={formDatas.missionId}
                                disabled={['editASO', 'editCREW'].includes(type)}
                            />
                        </InputLabel>
                    </div>
                    <div>
                        <InputLabel label="AIRCRAFT" name="aircraft">
                            <Select
                                name="aircraft"
                                options={aircrafts}
                                value={formDatas.aircraftId}
                                itemValue="id"
                                itemLabel="reg"
                                className="primary"
                                onChange={value =>
                                    updateForm(formDatas, setFormDatas, 'aircraftId', +value)
                                }
                                disabled={['editASO', 'editCREW'].includes(type)}
                            />
                        </InputLabel>
                    </div>
                    <div>
                        <InputLabel label="SEATS CAPACITY" name="seatsCapacity">
                            <Input
                                name="seatsCapacity"
                                type="number"
                                placeholder=""
                                defaultStyle="primary"
                                onChange={value =>
                                    updateForm(
                                        formDatas,
                                        setFormDatas,
                                        'passengerCapacity',
                                        value
                                    )
                                }
                                value={formDatas.passengerCapacity}
                                disabled={['editASO', 'editCREW'].includes(type)}
                            />
                        </InputLabel>
                    </div>
                </div>
                {isEdit && type !== 'editASO' && formDatas.id ? (
                    <AttachmentsSection flightId={formDatas.id} />
                ) : null}
                {isEdit && type !== 'editASO' && formDatas.id ? (
                    <ChecklistSection entityType="flight" entityId={formDatas.id} canEdit />
                ) : null}
            </div>
            <div className="m-auto p-20">
                <Button
                    text={`${action()} flight`}
                    disabled={false}
                    defaultStyle={`primary`}
                    onClick={() => validateData()}
                />
            </div>
            {stagePopup && (
                <div className="confirmDelete">
                    <div className="popup__content">
                        <h2>STAGE</h2>
                        <p>
                            Préremplir la date et les aéroports de départ/arrivée avec le stage
                            « {stagePopup.name} » ?
                        </p>
                        <div className="actions">
                            <Button
                                text="OUI, PRÉREMPLIR"
                                defaultStyle="primary"
                                onClick={prefillFromStage}
                            />
                            <p className="cancel" onClick={() => setStagePopup(null)}>
                                NON
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    ) : (
        <div>loading datas</div>
    );
};

export default FlightForm;
